import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import DepartmentForm from './DepartmentForm'
import { t } from '@/shared/lib/i18n'
import { departmentsPath, organizationPath } from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  readNewDepartmentSite,
  writeNewDepartmentSite,
} from '@/shared/lib/newDepartmentSiteSession'
import type { SharedProps } from '@/shared/types/SharedProps'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'

interface SiteOption {
  value: number
  label: string
}

interface Props {
  sites: SiteOption[]
  customDaySchedule: WeekdaySchedule
  parentDaySchedule: WeekdaySchedule
  [key: string]: unknown
}

const NewDepartmentPage = () => {
  const { sites, customDaySchedule, parentDaySchedule, currentTenantId, currentUserInfo } = usePage<
    Props & SharedProps
  >().props
  const currentUserId = currentUserInfo?.id ?? null
  const [storedSiteId] = useState(() => readNewDepartmentSite(currentTenantId, currentUserId))
  const isStoredSiteStale =
    storedSiteId != null && !sites.some((site) => site.value === storedSiteId)
  const rememberedSiteId = isStoredSiteStale ? null : storedSiteId

  // A remembered site that is no longer offered (deleted, disabled, out of scope) is dropped.
  useEffect(() => {
    if (isStoredSiteStale) writeNewDepartmentSite(currentTenantId, currentUserId, null)
  }, [isStoredSiteStale, currentTenantId, currentUserId])

  useBreadcrumbs([
    { label: t('nav.administration'), href: organizationPath() },
    { label: t('nav.departmentsAndUnits'), href: departmentsPath() },
    { label: t('nav.departments'), href: departmentsPath() },
    { label: t('organization.departments.newDepartment') },
  ])

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('organization.departments.newDepartment')}</h1>
      <DepartmentForm
        sites={sites}
        submitUrl={departmentsPath()}
        submitMethod="post"
        customDaySchedule={customDaySchedule}
        parentDaySchedule={parentDaySchedule}
        defaultSiteId={rememberedSiteId}
        onSiteChange={(siteId) => writeNewDepartmentSite(currentTenantId, currentUserId, siteId)}
      />
    </div>
  )
}

export default NewDepartmentPage
